import { importLibrary, setOptions } from "@googlemaps/js-api-loader"
import { Controller } from "@hotwired/stimulus"

import { countriesOverlapping, countryAt, indexCountries } from "../lib/country_shapes.js"
import { nearestMiles, positionFor, scoreFor, settleCloseTies } from "../lib/dealer_ranking.js"

/**
 * Where this storefront places, for every point on land.
 *
 * The three signals above the map are the rules. This is the page those rules produce,
 * and it has to be a map because the answer is different for every visitor: country is
 * the hundreds column of the score, distance the tens, brand strength the ones. Only the
 * last of those is the same everywhere - distance changes with every step and country
 * changes at every border. So the field is recomputed rather than rendered - one ranking
 * per cell, over the same payload OnlineDealers::RankingMapPayload hands the page.
 *
 * The arithmetic is not here. lib/dealer_ranking.js is the browser's copy of
 * OnlineDealerListing and RankingService, pinned to Ruby's own answers by
 * spec/fixtures/ranking_parity.json, and lib/country_shapes.js answers which country a
 * point stands in. This file moves, draws and toggles and does nothing else -
 * which is also why it says nothing to anybody. Every word on this form is ERB; there is
 * no user-facing copy in here and there must never be.
 *
 * The subject is drawn from the FORM, not from its stored row. Somebody is typing into
 * these fields and the point of the map is to answer for what they have typed, so the
 * payload's copy of this listing is dropped (that is what subject_id is for) and rebuilt
 * from the brand field, the country select and the location rows on every settled change.
 *
 * There is no country to pick, and that is the whole of it. The visitor's country is
 * wherever the CELL is, because the public page applies no country filter anywhere:
 * StorefrontListings hands RankingService every publishable listing on earth, and country
 * is only the hundreds column of the score. So a storefront places somewhere across the
 * border too - one hundred points lighter for not being local, but still holding whatever
 * its distance band and brand strength are worth against the field standing there. A map
 * that painted one country and left the sea and the neighbours identically blank was
 * answering a narrower question than the page asks, and could not show a Miami dealer
 * taking a position in the Bahamas.
 *
 * So every cell on LAND is painted and only water is left bare, water being the one place
 * a visitor cannot be. The single country still read off the form is the SUBJECT's - the
 * market this storefront serves, which is what decides whether it counts as local to the
 * cell being painted, exactly as OnlineDealerListing#same_country_as_user? decides it. It
 * frames the view as well: the map opens on that country and refits when it changes, which
 * is the navigation the viewing-country picker used to do before this drew the whole map.
 *
 * Expected markup (see _ranking_map.html.erb):
 *   <div data-controller="ranking-map"
 *        data-ranking-map-key-value="…" data-ranking-map-borders-url-value="…"
 *        data-ranking-map-subject-id-value="123" data-ranking-map-country-value="US">
 *     <script type="application/json" data-ranking-map-target="payload">…</script>
 *     <div data-ranking-map-target="canvas"></div>
 *   </div>
 */

// One cell is six pixels square. Smaller reads as a photograph of an answer the ranking
// cannot actually give - the tiers are hundreds of miles wide - and costs the square of
// the saving, since every cell is a full ranking over every listing on the page.
const CELL_PX = 6

// The cell loop yields the thread this often. A repaint is thousands of rankings and each
// ranking is a distance per branch, so an unchunked pass is measured in seconds; twelve
// milliseconds keeps inputs responsive while letting a country-sized repaint finish sooner.
const FRAME_BUDGET_MS = 12

// After the map settles, and after the form settles. The map's is the shorter of the two
// because a pan that has ended is done moving, where a field is still being typed into.
const IDLE_REDRAW_MS = 120
const FORM_SETTLE_MS = 150

// The tiers are stated in miles (OnlineDealerListing::DISTANCE_TIERS) and Circle takes
// metres.
const MILES_TO_METERS = 1609.344

// The globe's own bounds, and the same ones OnlineDealerLocation validates against - a
// coordinate outside them is a row the save will refuse, not a branch to rank from.
const MAX_LATITUDE = 90
const MAX_LONGITUDE = 180

// The frame the map is built with, and the one it returns to when the listing stops
// naming a country. Kept beside #fit rather than only in the constructor options, so the
// two cannot drift into disagreeing about what "the world view" is.
const WORLD_VIEW = { center: { lat: 0, lng: 0 }, zoom: 2 }

// How far out the opening frame reaches, as a multiple of the outermost distance ring's
// radius. Under 1 on purpose: one zoom step closer than the frame that holds every ring,
// so the near bands - the ones worth the most points - open large enough to read, and the
// outermost ring runs off the edges until the reader zooms out for it.
const RING_FRAME_MARGIN = 0.75

// Roughly constant along a meridian, and the scale #ringBounds widens by 1/cos(lat) for
// longitude. Held off the poles so that widening cannot divide by nothing.
const MILES_PER_DEGREE_LATITUDE = 69.05
const MAX_FRAME_LATITUDE = 85

// The ordinal ramp, indexed by a position's place among the REACHABLE ones - see
// positionColor.
// A single-hue ramp disappears into Google's own land and water colours, so the steps walk
// the spectrum instead, while leaving the subject's distance rings as the only red.
//
// Seven steps because seven is the most a dealer can reach, and it reaches that many only
// on a page with no house brand pins at all. Every pin takes a step off the end: the usual
// two leave five, and five steps of this list - navy, blue, green, gold, orange - are as
// far apart as a reader can be asked to tell colours apart on top of a map. A teal step
// used to sit between the blue and the green and was the one pair nobody could separate.
//
// Mirrored, value for value, by the legend chips in online_dealer_form.scss - the canvas
// is painted here and the legend is rendered in ERB, and there is no way to hand a colour
// from one to the other that does not put a hex literal or a user-facing string somewhere
// neither belongs. So the two files are one ramp written twice and must change together:
// a step edited here and not there leaves a legend quietly describing the wrong picture.
// The same note sits over the chips, pointing back at this line.
const POSITION_COLORS = ["#082f6f", "#0571b0", "#66a61e", "#c7a100", "#e17c05", "#7b3294", "#b45f7a"]
const FILL_ALPHA = 0.62

// Not a position: a cell this storefront does not reach. Grey out there is a real answer
// about a real visitor and not the unpainted margin it used to be. Mirrored as
// .online-dealer-map-chip-off.
const NEUTRAL_GREY = "#9aa0a6"

// Other dealers are reference points, not part of the ranking colour ramp. The near-black
// dot and white outline stay visible over the neutral and coloured map cells alike.
const COMPETITOR_DOT_COLOR = "#111827"
const COMPETITOR_DOT_OUTLINE_COLOR = "#ffffff"

// The brand red, matching $primary-red. Thinning outward so the nearest band - the one
// worth the most points - is the one that reads first.
const RING_COLOR = "#ed1a3b"
const RING_OPACITY_NEAREST = 0.7
const RING_OPACITY_FARTHEST = 0.2

const COMPETITOR_DOT_SCALE = 3

// Which fields the subject is rebuilt from. Matched on the suffix rather than on the full
// name because the location rows are indexed - locations_attributes[1756…][latitude] - and
// the index is minted in the browser by location-rows.
//
// Every one of them is a real form field now. There is nothing on this map a reader can
// change that is not part of the listing, which is what removing the viewing-country
// picker bought: exploring where this dealer shows is panning and zooming, and neither can
// ride a save.
const LIVE_FIELDS = /\[(branding_points|country|latitude|longitude|_destroy)\]$/

// The one change to the branches that no field event can carry: location-rows takes an
// unsaved row off the screen by removing it, and the coordinates in it stop existing
// without ever being edited. It says so on this event instead. Named rather than inlined
// because it is a contract with another controller - the prefix is that controller's
// Stimulus identifier, and the two halves have to move together.
const ROWS_CHANGED = "location-rows:removed"

// setOptions installs google.maps.importLibrary on the document and warns when it is asked
// twice. Turbo keeps this page's JavaScript context alive across visits, so connect() runs
// again on every return to the form; the key cannot differ between them.
let apiConfigured = false

/**
 * The colour a position is painted in.
 *
 * Indexed by the position's place among the ones a dealer can actually REACH, not by the
 * position number. positionFor can never return a pinned position - the pins have theirs
 * reserved before the organic list is cut, and the subject is shifted around them - so on
 * the usual page, positions 2 and 5 never come out of it and the ramp steps that would
 * have gone to them are steps nothing is ever painted in. Spending them anyway wasted two
 * of the seven on nothing and pushed the five that do get used closer together, which is
 * what made 3 and 4 indistinguishable. Counting the pins out gives the reachable ones the
 * whole ramp: 1, 3, 4, 6, 7 become steps 1 to 5.
 *
 * A page longer than the ramp keeps the ramp's last step rather than falling back to grey:
 * grey means off the page, and a position that is on it must never be drawn as one that
 * is not.
 *
 * @param {number|null} position 1-based position, null when the subject misses the page
 * @param {Array<{position: number}>} pins the payload's pins, in any order
 * @returns {string}
 */
function positionColor(position, pins) {
  if (position === null) return NEUTRAL_GREY

  // How many reserved positions this one sits behind. Subtracting them turns a page
  // position into a rank among the reachable ones, which is what the ramp is a list of.
  let reserved = 0
  for (let index = 0; index < pins.length; index += 1) {
    if (pins[index].position < position) reserved += 1
  }

  return POSITION_COLORS[Math.min(position - reserved, POSITION_COLORS.length) - 1]
}

function ringOpacity(index, count) {
  if (count < 2) return RING_OPACITY_NEAREST

  const span = RING_OPACITY_FARTHEST - RING_OPACITY_NEAREST

  return RING_OPACITY_NEAREST + (span * index) / (count - 1)
}

// Number("") is 0, which is a finite coordinate in the Gulf of Guinea. A row somebody is
// halfway through typing is not a branch, so a blank has to read as no coordinate.
//
// Bounded, because the fields unlock and a number off the globe is not a branch either:
// OnlineDealerLocation refuses latitude outside -90..90 and longitude outside -180..180,
// so drawing a marker and a ranking for one would be the map showing a placement that
// the save is about to reject. `limit` is the axis's own bound.
function coordinate(field, limit) {
  if (!field) return null

  const text = field.value.trim()
  if (text === "") return null

  const value = Number(text)

  return Number.isFinite(value) && Math.abs(value) <= limit ? value : null
}

/**
 * The canvas the field is painted on, as a map overlay.
 *
 * Built here rather than declared at module scope because OverlayView does not exist until
 * the maps library has loaded.
 *
 * The canvas is anchored to a LATITUDE AND LONGITUDE - whatever stood at the container's
 * top-left corner when the field was last painted - and not to the container. Anchoring it
 * to the container would drag the painted field along with a pan, so cells would claim to
 * describe ground they had been slid off. Anchored to the map, a pan uncovers unpainted
 * margin at the leading edge until the repaint lands, which is the honest of the two.
 *
 * @param {typeof google.maps.OverlayView} OverlayView
 * @param {typeof google.maps.Point} Point
 */
function overlayClass(OverlayView, Point) {
  return class PositionOverlay extends OverlayView {
    constructor(canvas) {
      super()
      this.canvas = canvas
      this.anchor = null
    }

    onAdd() {
      this.getPanes().overlayLayer.appendChild(this.canvas)
    }

    onRemove() {
      this.canvas.remove()
    }

    // Called by the map on every render, which during a drag is every frame. Only moves
    // the canvas; what is on it is the controller's business.
    draw() {
      const projection = this.getProjection()
      if (!projection || !this.anchor) return

      const point = projection.fromLatLngToDivPixel(this.anchor)
      if (!point) return

      this.canvas.style.left = `${point.x}px`
      this.canvas.style.top = `${point.y}px`
    }

    // Pins the canvas to the ground currently under the container's top-left corner, so a
    // pass can address its cells in container pixels.
    reanchor() {
      const projection = this.getProjection()
      if (!projection) return null

      this.anchor = projection.fromContainerPixelToLatLng(new Point(0, 0))
      this.draw()

      return projection
    }
  }
}

export default class extends Controller {
  static targets = ["canvas", "payload", "fallback", "summary", "summaryOptions"]
  static values = { key: String, bordersUrl: String, subjectId: String, country: String }

  connect() {
    this.payload = this.#parsePayload()
    if (!this.payload) return

    this.abort = new AbortController()
    this.shapes = []
    this.state = null
    this.stateKey = null
    // Stimulus reuses one instance when its element is disconnected and reconnected, so
    // this is cleared here rather than only initialised: a controller that came back
    // still holding the old flag would never build its map again.
    this.started = false

    // A refused save opens the pane holding the messages, which is often not this one, and
    // everything below is expensive: a script from Google, a quarter-megabyte of borders,
    // and a full pass over the ranking field. None of it is spent on a pane nobody has
    // looked at. Bootstrap fires shown.bs.tab on the tab BUTTON rather than on the pane -
    // the same thing online-dealer-tabs relies on to land a link inside a closed section.
    const pane = this.element.closest(".tab-pane")
    this.trigger = pane && document.querySelector(`[data-bs-target="#${pane.id}"]`)

    // Every show, not only the first. The first builds the map; the ones after it repaint,
    // because a closed pane is display:none and a canvas with no width cannot be painted
    // into - so coordinates edited while the pane is shut move the markers and the state
    // but leave the painted field as it was. Bringing the pane back is the moment there is a
    // box to paint in, and #start's own guard is what keeps this to one build.
    if (this.trigger) {
      this.onShown = () => {
        this.#start()
        this.#scheduleRepaint()
      }
      this.trigger.addEventListener("shown.bs.tab", this.onShown)
    }

    if (!pane || pane.classList.contains("active") || !this.trigger) this.#start()
  }

  disconnect() {
    clearTimeout(this.settleTimer)
    clearTimeout(this.repaintTimer)
    this.#cancelPass()

    if (this.abort) this.abort.abort()
    if (this.trigger && this.onShown) {
      this.trigger.removeEventListener("shown.bs.tab", this.onShown)
    }
    if (this.form && this.onFormChange) {
      this.form.removeEventListener("input", this.onFormChange)
      this.form.removeEventListener("change", this.onFormChange)
      this.form.removeEventListener(ROWS_CHANGED, this.onRowsChanged)
    }
    if (this.idleListener) this.idleListener.remove()
    if (this.boundsListener) this.boundsListener.remove()

    this.#clearShapes()
    if (this.overlay) this.overlay.setMap(null)
  }

  // The whole ranking field, out of the inert script tag beside the canvas. A payload that
  // will not parse leaves the container empty rather than half a map.
  #parsePayload() {
    if (!this.hasPayloadTarget) return null

    try {
      return JSON.parse(this.payloadTarget.textContent)
    } catch {
      return null
    }
  }

  async #start() {
    if (this.started) return
    this.started = true

    if (!apiConfigured) {
      setOptions({ key: this.keyValue })
      apiConfigured = true
    }

    // Held locally, because disconnect() aborts this one and a reconnect puts a fresh one
    // on the instance. Reading this.abort after the await would be asking the new visit's
    // question about the old visit's request.
    const abort = this.abort

    try {
      // Three libraries, not one: the classic Marker lives in "marker", and LatLngBounds,
      // Point and SymbolPath in "core". It is still one network round trip - the loader
      // bootstraps the API once and every importLibrary after that resolves out of it.
      const [maps, marker, core, borders] = await Promise.all([
        importLibrary("maps"),
        importLibrary("marker"),
        importLibrary("core"),
        fetch(this.bordersUrlValue, { signal: abort.signal }).then((response) => response.json()),
      ])

      // Disconnected while the network was busy - a Turbo visit away from the form.
      if (abort.signal.aborted) return

      this.#build(maps, marker, core, borders)
    } catch (error) {
      // Disconnected while the network was busy - nothing to show a pane that is gone.
      if (abort.signal.aborted) return

      // No new sentence is coined here: every word on this form is ERB, and the fallback
      // is the one the else-branch already prints for a blank key, sitting hidden beside
      // the canvas for exactly this. A rejected key, a Google outage or a borders file
      // that will not parse all land here, and the container swaps for it rather than
      // sitting empty. The error still goes to the console, where it belongs.
      this.#showFallback()

      // Logged rather than rethrown. #start is called without await - it is kicked off
      // from connect and from the tab shown handler - so a throw here does not reach any
      // catch: it becomes an unhandledrejection, which reports a failure this code has
      // just handled gracefully to whatever is watching for crashes, and makes the prose
      // fallback look like a broken page rather than the answer to one.
      console.error('Ranking map could not be drawn; showing the prose fallback.', error)
    }
  }

  // The canvas hides rather than empties: #build never ran, so there is nothing in it to
  // clear, and hiding is what makes room for the fallback sharing its space.
  #showFallback() {
    this.canvasTarget.hidden = true
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = false
  }

  #build(maps, marker, core, borders) {
    this.geometry = {
      Circle: maps.Circle,
      LatLngBounds: core.LatLngBounds,
      Marker: marker.Marker,
      Point: core.Point,
      SymbolPath: core.SymbolPath,
    }
    this.countries = indexCountries(borders)
    this.form = this.element.closest("form")

    // The stored copy of the subject is dropped here and rebuilt from the form. Everything
    // else on the page is the field it is ranked against, and none of it can be edited from
    // this screen, so it is read once. Kept on the instance rather than staying a local: id
    // is also RankingService's tiebreak, and #run needs the same number positionFor does.
    this.subjectId = Number(this.subjectIdValue)
    this.competitors = this.payload.listings.filter((listing) => listing.id !== this.subjectId)
    this.stored = this.payload.listings.find((listing) => listing.id === this.subjectId)

    this.map = new maps.Map(this.canvasTarget, {
      center: WORLD_VIEW.center,
      zoom: WORLD_VIEW.zoom,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    })

    // Bootstrap utilities rather than inline style - only left and top are set from script,
    // and those are pixel positions the map computes.
    const canvas = document.createElement("canvas")
    canvas.className = "position-absolute pe-none"
    this.overlay = new (overlayClass(maps.OverlayView, core.Point))(canvas)
    this.overlay.setMap(this.map)

    this.idleListener = this.map.addListener("idle", () => this.#scheduleRepaint())
    // A pass addresses its cells in container pixels, so a map that moves under it would
    // have it painting answers about the wrong ground. Moving cancels; idle restarts.
    this.boundsListener = this.map.addListener("bounds_changed", () => this.#cancelPass())

    if (this.form) {
      // One delegated pair rather than a listener per field: the location rows are cloned
      // and removed while the form is open, and a listener bound to a row that no longer
      // exists is the leak this avoids.
      this.onFormChange = (event) => this.#formChanged(event)
      this.form.addEventListener("input", this.onFormChange)
      this.form.addEventListener("change", this.onFormChange)

      // And the branch that leaves without any field being touched. Without this the
      // removed row keeps its marker, its rings and its share of every cell's ranking
      // until some other ranking field happens to be edited.
      this.onRowsChanged = () => this.#scheduleApply()
      this.form.addEventListener(ROWS_CHANGED, this.onRowsChanged)
    }

    this.#apply({ force: true })
  }

  #formChanged(event) {
    const name = event.target && event.target.name
    if (!name || !LIVE_FIELDS.test(name)) return

    this.#scheduleApply()
  }

  // One entry point for both ways the form can change, so a row removed in the middle of
  // typing coalesces with the typing instead of starting a pass of its own.
  #scheduleApply() {
    clearTimeout(this.settleTimer)
    this.settleTimer = setTimeout(() => this.#apply(), FORM_SETTLE_MS)
  }

  // Everything the map is about, off the form as it stands right now.
  #liveState() {
    const brandField = this.form && this.form.querySelector('[name$="[branding_points]"]')
    const countryField = this.form && this.form.querySelector('[name$="[country]"]')

    // The market this storefront sells to, off the form, because that is the field being
    // edited. It decides two things: whether the subject counts as local to the cell being
    // painted, and - because it is the only country the form names - where the map opens.
    // Blank is a listing with no market yet, which paints exactly as it should: the
    // storefront is local to nowhere, so it is one hundred points down everywhere at once.
    const country = countryField ? countryField.value : this.countryValue

    return {
      // parseInt, and the NaN it gives for an empty box is handed on unchanged.
      // effectiveBrand inside the ranking module is what decides an out-of-range or absent
      // figure, exactly as it decides one arriving in the payload; clamping here as well
      // would be a second opinion about the same rule.
      brand: brandField ? parseInt(brandField.value, 10) : this.#storedBrand(),
      country,
      branches: this.#branches(),
    }
  }

  #storedBrand() {
    return this.stored ? this.stored.brand : NaN
  }

  // A row marked for destruction is already gone as far as the ranking is concerned - the
  // save will destroy it - so the map stops measuring from it the moment the box is ticked
  // rather than after a round trip.
  #branches() {
    if (!this.form) return []

    return [...this.form.querySelectorAll("[data-location-row]")].flatMap((row) => {
      const destroy = row.querySelector('input[type="checkbox"][name$="[_destroy]"]')
      if (destroy && destroy.checked) return []

      const lat = coordinate(row.querySelector('[name$="[latitude]"]'), MAX_LATITUDE)
      const lng = coordinate(row.querySelector('[name$="[longitude]"]'), MAX_LONGITUDE)

      return lat === null || lng === null ? [] : [{ lat, lng }]
    })
  }

  #apply({ force = false } = {}) {
    const state = this.#liveState()
    const key = JSON.stringify(state)
    if (!force && key === this.stateKey) return

    // The frame moves with the market the listing is FILED UNDER, which is the only
    // country the form still names. Correcting it from France to Germany is a statement
    // that this storefront's page is the German one, so showing Germany is the answer to
    // the question that edit asked. Nothing else refits - a pan is the reader's, and a
    // repaint must never take it back off them.
    //
    // The first branch is the other statement of where: a new listing that gains its
    // first coordinates has just said where it is, and the frame goes there.
    const moved =
      force ||
      !this.state ||
      state.country !== this.state.country ||
      (this.state.branches.length === 0 && state.branches.length > 0)
    this.state = state
    this.stateKey = key

    // A pass in flight is answering the question the lines above have just replaced.
    // Cancelled here rather than left to #repaint's own cancel a redraw later, which would
    // let it go on painting cells from a state that is already gone.
    this.#cancelPass()
    this.#rebuildShapes()
    if (moved) this.#fit()
    this.#scheduleRepaint()
    this.#describeSubjectPosition()
  }

  // The one number a screen reader gets out of a picture that answers a different
  // question for every point on Earth - a full transcript of that is not a sentence
  // anyone could read. This states the single most relevant point on it instead: where
  // the storefront would place in its OWN country, measured from its OWN nearest
  // branch. Run synchronously off the same state #apply just settled, rather than
  // waiting on the chunked grid pass #scheduleRepaint kicks off - the sentence a
  // screen reader needs is one ranking, not thousands of them, and it should not lag
  // behind an edit the way the painted field's own frame budget makes it.
  #describeSubjectPosition() {
    if (!this.hasSummaryTarget || !this.hasSummaryOptionsTarget) return

    const eligible = this.payload.subject_eligible && this.state.branches.length > 0 && Boolean(this.state.country)
    const position = eligible ? this.#subjectPosition() : null
    const key = position === null ? "off" : String(position)

    const option = this.summaryOptionsTarget.querySelector(`[data-position="${key}"]`)
    this.summaryTarget.textContent = option ? option.textContent.trim() : ""
  }

  // Where this storefront places for a visitor standing at its own first branch, in
  // its own market: sameCountry is always true here and the distance from that branch
  // to itself is the shortest one Cartography can return, which is exactly "home
  // ground" read as a ranking rather than as a figure of speech. this.competitors
  // already carries id, brand, branches and country straight off the payload - the
  // stripped, id-bearing arrays #run builds for the pixel-grid pass are not needed for
  // one ranking done once per settle instead of once per cell.
  #subjectPosition() {
    // Every branch, not whichever row happens to be first. The subject scores the same
    // standing at any of them - it is on one, so its own distance is nought either way -
    // but the COMPETITORS' distances are measured from wherever it stands, so a listing
    // with two widely separated branches announced a different position depending on
    // which row came first. Row order is the order somebody added branches in. It is not
    // geography and it must not decide the answer.
    //
    // The best of them is the answer: somebody weighing a dealer wants the strongest
    // showing it can make, not the one its first row happens to make. The sentence says
    // so - summary_ranked_text names the branch that places it best, rather than "its
    // nearest branch", which described a rule this does not follow once the rivals differ
    // from one branch to the next. null from every branch is a storefront off the page
    // wherever it stands, which is the honest answer to the same question.
    const positions = this.state.branches
      .map((branch) => this.#positionFrom(branch))
      .filter((position) => position !== null)

    return positions.length === 0 ? null : Math.min(...positions)
  }

  // Where the storefront places for a visitor standing at this one branch, in its own
  // market. sameCountry is true by construction rather than read off the map: the
  // sentence this feeds says "in its own country", and a branch sitting outside the
  // market it serves is a real shape - the answer is the best case that phrasing states,
  // not a reading of where the branch itself is.
  #positionFrom(branch) {
    const constants = this.payload.constants

    const fastMiles = nearestMiles(this.state.branches, branch.lat, branch.lng, constants.tiers)
    const subject = scoreFor({ brand: this.state.brand, miles: fastMiles, sameCountry: true }, constants)
    const miles = this.competitors.map((listing) =>
      nearestMiles(listing.branches, branch.lat, branch.lng, constants.tiers),
    )
    const scores = this.competitors.map((listing, index) =>
      scoreFor(
        { brand: listing.brand, miles: miles[index], sameCountry: listing.country === this.state.country },
        constants,
      ),
    )
    const ids = this.competitors.map((listing) => listing.id)
    const subjectMiles = settleCloseTies(
      subject, fastMiles, this.state.branches, scores, miles, this.competitors, branch.lat, branch.lng,
    )

    return positionFor(subject, subjectMiles, this.subjectId, scores, miles, ids, this.payload.pins, constants.max_dealers)
  }

  // The subject's own branches in view, close enough to read the near rings, which is
  // where a reader starts: the first question anybody asks of this map is how the
  // storefront does around its own branches, and the answers further out are the ones
  // they pan to afterwards. Framing the whole market instead opened a US listing on a
  // continent where the rings were specks.
  //
  // A listing with no coordinates yet has no rings to frame, and falls back to its market.
  //
  // A listing that names no country the border file holds is shown the world, which is
  // the honest frame for a storefront that has not said where it sells - or has just
  // stopped saying. Returning early instead left the map wherever it had last been
  // fitted, so clearing the country on a French listing kept France on screen while the
  // ranking underneath had gone local nowhere.
  #fit() {
    if (this.state.branches.length > 0) {
      this.map.fitBounds(this.#ringBounds())
      return
    }

    const home = this.countries.get(this.state.country)
    if (!home) {
      this.map.setCenter(WORLD_VIEW.center)
      this.map.setZoom(WORLD_VIEW.zoom)
      return
    }

    // #view rather than #bbox: the box that ENCLOSES a country either side of the
    // antimeridian is the whole world, and opening Russia or Fiji on the globe is not
    // showing anybody their market. See viewOf in country_shapes.js.
    const { south, north } = home.bbox
    const { west, east } = home.view
    const bounds = new this.geometry.LatLngBounds({ lat: south, lng: west }, { lat: north, lng: east })

    this.map.fitBounds(bounds)
  }

  // Every branch's outermost ring, scaled by RING_FRAME_MARGIN. Worked out from the
  // tiers rather than read back off the Circles so it does not depend on #rebuildShapes
  // having run first.
  #ringBounds() {
    const { tiers } = this.payload.constants
    const reach = Math.max(...tiers.map((tier) => tier.max_miles)) * RING_FRAME_MARGIN
    const latSpan = reach / MILES_PER_DEGREE_LATITUDE
    const bounds = new this.geometry.LatLngBounds()

    for (const { lat, lng } of this.state.branches) {
      const cos = Math.cos((Math.min(Math.abs(lat), MAX_FRAME_LATITUDE) * Math.PI) / 180)
      const lngSpan = Math.min(latSpan / cos, MAX_LONGITUDE)
      bounds.extend({ lat: Math.max(lat - latSpan, -MAX_FRAME_LATITUDE), lng: lng - lngSpan })
      bounds.extend({ lat: Math.min(lat + latSpan, MAX_FRAME_LATITUDE), lng: lng + lngSpan })
    }

    return bounds
  }

  #rebuildShapes() {
    this.#clearShapes()

    const { tiers } = this.payload.constants

    for (const branch of this.state.branches) {
      // Classic Marker, deprecated though it is: AdvancedMarkerElement will not render
      // without a cloud Map ID, and this app has none to give it - a browser key is the
      // whole of what is configured. A console deprecation notice is the price of a marker
      // that appears at all.
      this.shapes.push(new this.geometry.Marker({ position: branch, map: this.map, zIndex: 2 }))

      tiers.forEach((tier, index) => {
        this.shapes.push(
          new this.geometry.Circle({
            center: branch,
            clickable: false,
            fillOpacity: 0,
            map: this.map,
            radius: tier.max_miles * MILES_TO_METERS,
            strokeColor: RING_COLOR,
            strokeOpacity: ringOpacity(index, tiers.length),
            strokeWeight: 1,
            zIndex: 1,
          }),
        )
      })
    }

    // Where everybody else stands. Dots rather than pins: they are the field, not the
    // subject, and seventy of them drawn as markers would be the loudest thing on screen.
    //
    // Each one carries its dealer's name as rollover text, because a dot is otherwise a
    // position with nobody standing on it - the reader can see that somebody is nearer to
    // a cell than this storefront is, and not who. clickable and unoptimized are what that
    // costs: a marker with mouse events turned off is not hovered, and an optimized one is
    // drawn into a shared canvas with no element of its own to hang a tooltip on. Nothing
    // is bound to the click - cursor stays the map's own arrow, so the name is all a
    // hover offers and the dot does not read as something to press.
    for (const listing of this.competitors) {
      for (const branch of listing.branches) {
        this.shapes.push(
          new this.geometry.Marker({
            clickable: true,
            cursor: "default",
            optimized: false,
            title: listing.title,
            icon: {
              fillColor: COMPETITOR_DOT_COLOR,
              fillOpacity: 1,
              path: this.geometry.SymbolPath.CIRCLE,
              scale: COMPETITOR_DOT_SCALE,
              strokeColor: COMPETITOR_DOT_OUTLINE_COLOR,
              strokeWeight: 1.5,
            },
            map: this.map,
            position: branch,
            zIndex: 0,
          }),
        )
      }
    }
  }

  // setMap(null) on every one of them. A Circle or a Marker dropped without it stays on the
  // map and stays in memory, and this runs on every settled keystroke - five tiers times
  // four branches is twenty circles a character.
  #clearShapes() {
    for (const shape of this.shapes || []) shape.setMap(null)
    this.shapes = []
  }

  #scheduleRepaint() {
    clearTimeout(this.repaintTimer)
    this.repaintTimer = setTimeout(() => this.#repaint(), IDLE_REDRAW_MS)
  }

  #cancelPass() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  #repaint() {
    this.#cancelPass()
    if (!this.overlay || !this.state) return

    const projection = this.overlay.reanchor()
    const width = this.canvasTarget.clientWidth
    const height = this.canvasTarget.clientHeight
    if (!projection || !width || !height) return

    // Only when it has actually changed. Assigning width clears the canvas even when the
    // value is the same, and clearing it here is what would make a repaint blink.
    const canvas = this.overlay.canvas
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }

    // Which countries can appear in this view, out of the hundred and seventy-five. Asked
    // once per pass rather than once per cell: the box is the map's and does not move while
    // the pass runs, and it cuts the per-cell scan to the handful of neighbours on screen.
    const box = this.#viewportBox()
    if (!box) return

    // Cleared once, here, rather than left to hold the previous answer while the new one
    // is worked out. reanchor() has just pinned the canvas to whatever ground is under the
    // container's top-left corner NOW, so after a pan the old cells describe ground they
    // have been slid off - keeping them on screen would be showing a stale answer about
    // the wrong place, which is the one thing a map of an ordering must not do.
    const visible = canvas.getContext("2d")
    visible.clearRect(0, 0, width, height)

    // Painted into a buffer and blitted per frame rather than cell by cell onto the canvas
    // the reader is looking at. The pass is chunked across frames either way; what the
    // buffer buys is one composited draw per frame instead of thousands of six-pixel fills
    // against a live layer, and one place for the alpha to be set.
    const buffer = document.createElement("canvas")
    buffer.width = width
    buffer.height = height

    const context = buffer.getContext("2d")
    context.globalAlpha = FILL_ALPHA

    this.#run(context, projection, countriesOverlapping(box, this.countries), {
      columns: Math.ceil(width / CELL_PX),
      rows: Math.ceil(height / CELL_PX),
      // Cleared before each blit, not just once before the pass. The buffer holds every
      // cell painted so far and its cells are translucent, so compositing it over a
      // canvas that still carries the previous frame lays the early cells down again and
      // again - they end up darker than the late ones, and the colour a reader matches
      // against the legend becomes a function of how many frames the pass happened to
      // take. One clearRect per frame, against thousands of fills, costs nothing.
      onFrame: () => {
        visible.clearRect(0, 0, width, height)
        visible.drawImage(buffer, 0, 0)
      },
    })
  }

  /**
   * The ground on screen, as a box countriesOverlapping can cut against.
   *
   * @returns {{south: number, west: number, north: number, east: number}|null} null before
   *   the map has laid out, when it has no bounds to report
   */
  #viewportBox() {
    const bounds = this.map.getBounds()
    if (!bounds) return null

    const southWest = bounds.getSouthWest()
    const northEast = bounds.getNorthEast()
    const west = southWest.lng()
    const east = northEast.lng()

    // A view straddling the antimeridian reports its east edge WEST of its west edge.
    // Widened to the whole range rather than split into two boxes: the cost is a few more
    // countries for countryAt to reject per cell, and the alternative is a second code path
    // through everything below for the one view that crosses the date line.
    return {
      south: southWest.lat(),
      north: northEast.lat(),
      west: east < west ? -180 : west,
      east: east < west ? 180 : east,
    }
  }

  // The pass, in frames. Every cell is a full ranking over every listing on the page, and
  // every ranking is a distance per branch, so the cost is cells times branches - a busy
  // field is tens of milliseconds of arithmetic and it cannot be allowed to hold the thread
  // while somebody is typing.
  #run(context, projection, candidates, { columns, rows, onFrame }) {
    const total = columns * rows
    // Everything about a listing that does not move with the cell, lifted out of the inner
    // loop. The distance band is left to compute per cell, and so is whether the listing is
    // local - which is a fact about the CELL now, not about the map, so the country it is
    // compared against is carried rather than the comparison's answer.
    const pass = {
      candidates,
      context,
      projection,
      // Read twice per cell - once to place the subject around the reserved positions,
      // once to colour what comes out - so it is lifted onto the pass with everything else
      // that does not move while it runs.
      pins: this.payload.pins,
      field: this.competitors.map((listing) => ({
        brand: listing.brand,
        branches: listing.branches,
        country: listing.country,
      })),
      // Parallel to `field` and to the `scores` and `miles` arrays below, and read only by
      // positionFor's last tiebreak - RankingService.rank_dealers sorts ties on distance
      // and then by id, and this is the same number for the same reason. Lifted out once
      // rather than mapped off `field` per cell, matching everything else on the pass that
      // does not move.
      ids: this.competitors.map((listing) => listing.id),
      subject: {
        id: this.subjectId,
        brand: this.state.brand,
        branches: this.state.branches,
        country: this.state.country,
      },
      // Two facts, not one, because they are settled in different places.
      // OnlineDealers::CompletenessPolicy's :country and :locations are read live off the
      // form, matching everything else here that redraws as somebody types - that is the
      // whole reason this map exists. subject_eligible is the rest of the test the public
      // page applies: archived, the sales gate, and the completeness facts this PANE does
      // not edit. RankingMapPayload settles those once rather than the map guessing at
      // them, which is the point - the alternative is completeness reimplemented here and
      // free to disagree with the server that owns it.
      //
      // The cost is that a title or a logo cleared on another tab does not reach this
      // flag until the save. Deliberate; the reasoning is on
      // RankingMapPayload#subject_eligible?, which is where the line is drawn.
      //
      // Either withholds a position; see #paintCell.
      subjectEligible:
        this.payload.subject_eligible &&
        this.state.branches.length > 0 &&
        Boolean(this.state.country),
    }
    // Two arrays, refilled per cell rather than allocated per cell. positionFor only
    // reads them: the scores, and the distances that break a tie between them.
    pass.scores = new Array(pass.field.length)
    pass.miles = new Array(pass.field.length)

    let index = 0

    const step = () => {
      const started = performance.now()

      while (index < total) {
        const column = index % columns
        this.#paintCell(pass, column, (index - column) / columns)
        index += 1

        if (performance.now() - started >= FRAME_BUDGET_MS) break
      }

      // Every frame, not only the last, so the field fills in as it is worked out. Blitting
      // only on completion would leave the map blank for the whole pass, which reads as a
      // map that has stopped rather than one that is thinking.
      onFrame()

      if (index < total) {
        this.frame = requestAnimationFrame(step)
      } else {
        this.frame = null
      }
    }

    this.frame = requestAnimationFrame(step)
  }

  #paintCell(pass, column, row) {
    const x = column * CELL_PX
    const y = row * CELL_PX
    const at = pass.projection.fromContainerPixelToLatLng(
      new this.geometry.Point(x + CELL_PX / 2, y + CELL_PX / 2),
    )
    if (!at) return

    const lat = at.lat()
    const lng = at.lng()

    // Where the visitor this cell is about would be geolocated to. Water is the only ground
    // with no answer, and the only ground left unpainted: nobody stands in the sea, so a
    // cell painted there would be answering for somebody who does not exist - which on a
    // colour ramp reads exactly like an answer that does.
    const country = countryAt(lat, lng, pass.candidates)
    if (!country) return

    // A subject with no branch left or no country set fails
    // OnlineDealers::CompletenessPolicy - :locations or :country is missing - and
    // RankingService#rank_dealers never sees it: #publishable? gates the whole method
    // before the sort runs. nearestMiles([]) still returns a distance-band tier of zero
    // rather than "excluded", so without this the pass below would score the edit that
    // just emptied the last branch row and paint it a numbered place on every cell on
    // Earth - a position the public page cannot produce because the listing is not on
    // it to have one.
    if (!pass.subjectEligible) {
      pass.context.fillStyle = NEUTRAL_GREY
      pass.context.fillRect(x, y, CELL_PX, CELL_PX)
      return
    }

    // The hundreds column, decided per cell. This is OnlineDealerListing#same_country_as_user?
    // with the visitor's country read off the map instead of off a GeoipCache, which is the
    // whole reason the neighbours can be painted at all.
    const iso2 = country.iso2
    const constants = this.payload.constants
    const fastMiles = nearestMiles(pass.subject.branches, lat, lng, constants.tiers)
    const subject = scoreFor(
      { brand: pass.subject.brand, miles: fastMiles, sameCountry: pass.subject.country === iso2 },
      constants,
    )

    for (let rival = 0; rival < pass.field.length; rival += 1) {
      pass.miles[rival] = nearestMiles(pass.field[rival].branches, lat, lng, constants.tiers)
      pass.scores[rival] = scoreFor(
        {
          brand: pass.field[rival].brand,
          miles: pass.miles[rival],
          sameCountry: pass.field[rival].country === iso2,
        },
        constants,
      )
    }

    const subjectMiles = settleCloseTies(
      subject, fastMiles, pass.subject.branches, pass.scores, pass.miles, pass.field, lat, lng,
    )
    const position = positionFor(
      subject,
      subjectMiles,
      pass.subject.id,
      pass.scores,
      pass.miles,
      pass.ids,
      pass.pins,
      constants.max_dealers,
    )
    pass.context.fillStyle = positionColor(position, pass.pins)
    pass.context.fillRect(x, y, CELL_PX, CELL_PX)
  }
}
